import { Suspense } from 'react';

import { CollinsWidget } from '@/ui';
import { getChannel, getCollinsWidgetData, getDocument, getPlaceId } from '@lib/server';
import { Place } from '@types';

import { Wrapper } from './utils';

export interface DesignMyNightProps {
  bookingTypes: string;
  showSearchField: boolean;
  customSource?: string;
  showTypeFirst: boolean;
  places: [{ linkType: 'Entry'; id: string; type: 'Link' }];
  domain: string;
}

export async function DesignMyNight({
  params: { domain, venue },
  id,
  ...data
}: {
  params: { domain: string; venue?: string };
  id?: string;
} & DesignMyNightProps) {
  const channel = await getChannel(decodeURIComponent(domain));

  if (!channel?.channelId) {
    return null;
  }

  let venueId = undefined;
  const gtmId = channel.apps?.googleTagManager?.active ? channel.apps.googleTagManager.id : undefined;

  // if no venues are specified and on venue level
  if (venue && !data?.places) {
    const { placeId } = await getPlaceId(channel, decodeURIComponent(venue));

    if (!placeId) {
      return null;
    }

    const venueData = await getDocument<Place>('place', placeId, {
      tags: [channel.channelId],
      depth: 0,
      select: ['fields.apps.designMyNight', 'id'],
    });

    venueId = venueData?.fields?.apps?.designMyNight?.id;
    // if venues are specified
  } else if (Boolean(data?.places?.length)) {
    // get the places assigned to the widget
    const placesData = await Promise.all(
      data?.places.map(async (place) => {
        const placeData = await getDocument<Place>('place', place?.id, {
          tags: [channel.channelId],
          depth: 1,
          status: 'published',
          select: ['fields.apps.designMyNight', 'fields.name'],
        });

        if (placeData) {
          return placeData?.fields?.apps?.designMyNight?.id;
        }
      }),
    );

    venueId = placesData?.join(',');
    // fallback to all venues
  } else {
    const { venues } = await getCollinsWidgetData(decodeURIComponent(domain));

    venueId = venues.map((venue) => venue.id).join(',');
  }

  if (!venueId) {
    return null;
  }

  return (
    <Wrapper id={id}>
      {/* suspense added for search params */}
      <Suspense>
        <CollinsWidget
          channelId={channel.channelId}
          className="w-full max-w-xl"
          venue={venueId}
          googleTagManagerCode={gtmId}
          allowedTypes={data?.bookingTypes}
          searchVenues={data?.showSearchField}
          showTypeFirst={data?.showTypeFirst}
          customSource={data?.customSource}
          returnUrl={`https://${domain}/api/booking${venue ? `?venue=${venue}` : ''}`}
        />
      </Suspense>
    </Wrapper>
  );
}
